import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Calendar from 'react-calendar';
import {
  compose,
  setDisplayName,
} from 'recompose';
import moment from 'moment';
import { compact } from 'lodash';
import ReactQuill from 'react-quill';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Send from '@material-ui/icons/Send';
import CalendarToday from '@material-ui/icons/CalendarToday';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Popover, PopoverBody, Button as RSButton } from 'reactstrap';

import immutableToJS from 'helpers/immutableToJS';
import {
  dispatchRestMaker,
  getDeliveryScheduleStatus,
  toDefaultISO,
  toISO,
  toShortestDateFormat,
  toDefaultTimeFormat,
} from 'helpers/utils';
import {
  injectIntl,
  Paper,
  TagsInput,
} from 'components';
import { broadCastGroupActions, singleBroadCastGroupActions } from 'redux/users/actions';
import { broadcastActions } from 'redux/mails/actions';
import { getRequestingSelector, selectState } from 'redux/selectors';
import CustomerToSend from './CustomerToSend';
import BroadcastStatsDialog from './BroadcastStatsDialog';

const styles = theme => ({
  cancelBtn: { borderRadius: 5 },
  cancelBtnLabel: {
    color: '#6999e8',
    fontSize: 12,
    fontWeight: 600,
  },
  datePicker: {
    display: 'inline-block',
    fontSize: '1rem',
    marginTop: 10,
    textAlign: 'center',
    marginRight: 10,
    [theme.breakpoints.down('lg')]: {
      marginRight: 5,
      width: 200,
    },
    [theme.breakpoints.down('md')]: {
      marginRight: 5,
      width: 170,
    },
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      width: '90%',
    },
  },
  pickerInput: {
    fontSize: '1rem',
    fontWeight: 400,
    // width: 150,
  },
  red: {
    color: 'red',
  },
  rightIcon: {
    color: '#fff',
    marginLeft: theme.spacing.unit,
  },
  sendBtn: {
    backgroundColor: '#6999e8',
    borderRadius: 5,
  },
  sendBtnLabel: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
  },
  statsBtn: {
    minHeight: 35,
    borderRadius: 5,
    backgroundColor: 'white',
    color: '#09f',
    marginTop: 10,
    maxHeight: '55px',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
  },
  timePicker: {
    display: 'inline-block',
    fontSize: '1rem',
    marginTop: 10,
    textAlign: 'left',
    width: 140,
    [theme.breakpoints.down('md')]: {
      width: 120,
    },
    [theme.breakpoints.down('xs')]: {
      width: 160,
      textAlign: 'start',
    },
  },
  inputTimePicker: {
    [theme.breakpoints.down('xs')]: {
      width: 160,
    },
  },
});

const initialState = {
  broadcastStatsOpen: false,
  composerText: '',
  cutOffDate: new Date(),
  cutOffTime: moment().format('hh:mm'),
  deliveryDate: '',
  recipient: null,
  subjectText: '',
  deliveryCalendar: false,
  cutOffCalendar: false,
  deliveryIndicator: false,
  cutOffIndicator: false,
};

class Composer extends Component {
  static propTypes = {
    broadcastRequesting: PropTypes.bool,
    classes: PropTypes.shape({}).isRequired,
    isVendor: PropTypes.bool,
    userContacts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ).isRequired,
    userBroadCastGroups: PropTypes.arrayOf(
      PropTypes.string,
    ).isRequired,

    broadcast: PropTypes.func.isRequired,
    formatMessage: PropTypes.func.isRequired,
    readUserBroadCastGroup: PropTypes.func.isRequired,
    updateUserBroadCastGroup: PropTypes.func.isRequired,
    updateSingleBroadCastGroup: PropTypes.func.isRequired,
  };

  static defaultProps = {
    broadcastRequesting: false,
    isVendor: false,
  };

  static contextTypes = {
    readOnly: PropTypes.bool,
    currentUser: PropTypes.shape({}),
  };

  state = initialState;

  componentWillMount() {
    const { isVendor, readUserBroadCastGroup } = this.props;
    readUserBroadCastGroup('');

    if (!isVendor) {
      this.composeSubject();
      this.setDefaultContent();
    }
  }
  onChangeTags = (tags) => {
    const { updateUserBroadCastGroup } = this.props;
    updateUserBroadCastGroup('', tags);
  }

  onEditTags = (tag, prefix, newValue, updateState) => {
    const { updateSingleBroadCastGroup, readUserBroadCastGroup } = this.props;
    updateSingleBroadCastGroup('', {
      oldGroup: tag,
      newGroup: `${prefix}${newValue}`,
      onSuccess: () => {
        updateState();
        readUserBroadCastGroup('');
      },
    });
  }

  handleBroadcastStatsClose = () => {
    this.setState({ broadcastStatsOpen: false });
  }

  handleBroadcastStatsOpen = () => {
    this.setState({ broadcastStatsOpen: true });
  }

  handleChangeComposer = (value) => {
    this.setState({ composerText: value });
  }

  handleChangeSubject = (event) => {
    this.setState({ subjectText: event.target.value });
  }

  handleCutOffDate = (value) => {
    const { deliveryDate } = this.state;
    const updateState = { cutOffDate: value };
    if (deliveryDate && deliveryDate.getTime() < new Date(value).getTime()) {
      updateState.cutOffDate = deliveryDate;
    }
    this.setState(updateState, this.composeSubject);
  }

  handleCutOffTime = (event) => {
    const { value } = event.target;
    this.setState({ cutOffTime: value }, this.composeSubject);
  }

  handleDeliveryDate = (value) => {
    const { cutOffDate } = this.state;
    const updateState = { deliveryDate: value };
    if (value.getTime() > cutOffDate.getTime()) {
      updateState.cutOffDate = value;
    }
    this.setState(updateState, this.composeSubject);
  }

  broadcast = () => {
    const { broadcast, isVendor } = this.props;
    const {
      composerText,
      cutOffTime,
      cutOffDate,
      deliveryDate,
      recipient,
      subjectText,
    } = this.state;
    const {
      currentUser,
      readOnly,
    } = this.context;

    const payload = {
      subject: subjectText,
      html: composerText,
      isVendor,
    };

    if (!isVendor) {
      const parts = cutOffTime.split(':');
      const hour = parseInt(parts[0], 10);
      const minute = parseInt(parts[1], 10);
      const cutOffTs = moment(cutOffDate).hour(hour).minute(minute);

      payload.cutOffTime = cutOffTs.startOf('minute');
      payload.cutOffDate = moment(cutOffDate).startOf('day');
      payload.cutOffTs = cutOffTime;
      payload.deliveryDate = moment(deliveryDate).startOf('day');
    }
    if (recipient.isGroup) {
      broadcast({
        ...payload,
        groupName: recipient.groupName,
        onSuccess: this.clearFields,
      });
    } else {
      broadcast({
        ...payload,
        email: recipient.email,
        onSuccess: this.clearFields,
      });
    }
  }

  clearFields = () => {
    this.setState(initialState);
  };

  composeSubject = () => {
    const { deliveryDate, cutOffDate, cutOffTime } = this.state;
    const {
      currentUser,
    } = this.context;
    if (!deliveryDate) {
      this.setState({ subjectText: '' });
      return;
    }
    const originalSubjectString = `Ordering link for ${(currentUser.account.profile.companyName)}, Delivery: ${toShortestDateFormat(deliveryDate)}, Cut-off time: ${toShortestDateFormat(cutOffDate)} ${cutOffTime ? moment(cutOffTime, 'hh:mm a').format('LT') : '(Invalid Time)'}`;
    this.setState({ subjectText: originalSubjectString });
  }

  setDefaultContent = () => {
    const { formatMessage} = this.props;

    this.setState({
      composerText: formatMessage(`Click the blue link to view today\'s availability list and order.`),
    });

  }

  selectCustomerToSend = (recipient) => {
    const { formatMessage, isVendor } = this.props;
    this.setState({ recipient });
  }

  generalToggler = (name, indicatorName) => {
    const toggleTarget = this.state[name];
    const indicatorTarget = this.state[indicatorName];
    this.setState({ [name]: !toggleTarget, [indicatorName]: !indicatorTarget });
  }
  indicatorOnlyToggle = (indicatorName) => {
    const indicatorTarget = this.state[indicatorName];
    this.setState({ [indicatorName]: !indicatorTarget });
  }

  render() {
    const {
      broadcastRequesting,
      classes,
      formatMessage,
      isVendor,
      userBroadCastGroups,
      userContacts,
    } = this.props;
    const {
      currentUser,
      readOnly,
    } = this.context;
    const {
      composerText,
      cutOffDate,
      cutOffTime,
      deliveryDate,
      recipient,
      subjectText,
      broadcastStatsOpen,
      deliveryCalendar,
      cutOffCalendar,
      deliveryIndicator,
      cutOffIndicator,
    } = this.state;
    const currentTime = toDefaultTimeFormat(new Date()).toString();
    const deliverySchedule = currentUser.account.profile.deliverySchedule || [];
    const isInvalid = cutOffTime.length < 1 || cutOffDate.length < 1 || deliveryDate.length < 1;

    return (
      <div>
        <div className="broadcast-pane">
          <Paper className="broadcast-paper">
            <div className="broadcast-content">
              <div className="broadcast-composer">
                <div className="broadcast-who-date">
                  {/* <div className="section-title mb-2">
                    {isVendor ? formatMessage('Vendor Broadcast') : formatMessage('Customer Broadcast')}
                  </div> */}
                  <div className="broadcast">
                    <CustomerToSend
                      formatMessage={formatMessage}
                      isVendor={isVendor}
                      onSelect={this.selectCustomerToSend}
                      userBroadCastGroups={userBroadCastGroups}
                      userContacts={userContacts}
                      value={recipient}
                    />
                    {!isVendor && (
                      <div className="broadcast-date">
                        <form
                          className={classes.container}
                          noValidate
                        >
                          <TextField
                            className={classes.datePicker}
                            id="delivery-date"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            autoComplete="off"
                            onFocus={() => this.indicatorOnlyToggle('deliveryIndicator')}
                            onBlur={() => this.indicatorOnlyToggle('deliveryIndicator')}
                            InputProps={{
                              classes: { input: classes.pickerInput },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <RSButton
                                    style={{
                                      background: 'none',
                                    }}
                                    className="d-flex"
                                    color="none"
                                    onClick={() => this.generalToggler('deliveryCalendar', 'deliveryIndicator')}
                                    type="button"
                                    ref={(c) => { this.delivery = c; }}
                                    id="delivery-calendar">
                                    <CalendarToday
                                      className={`calendar-toggle-icon ${deliveryIndicator && 'indicate'}`}
                                    />
                                  </RSButton>
                                </InputAdornment>
                              ),
                            }}
                            label={<div> <span style={{color:'#ff0000'}}> *</span> {formatMessage('Delivery or Pick-Up Date')}</div>}
                            variant="outlined"
                            value={deliveryDate && toDefaultISO(deliveryDate)}
                          />
                        </form>
                        <div className="order-cut-off-date">
                          <form
                            className={classes.container}
                            noValidate
                          >
                            <TextField
                              className={classes.datePicker}
                              id="cut-off-date"
                              InputLabelProps={{
                                shrink: true,
                              }}
                              autoComplete="off"
                              onFocus={() => this.indicatorOnlyToggle('cutOffIndicator')}
                              onBlur={() => this.indicatorOnlyToggle('cutOffIndicator')}
                              InputProps={{
                                classes: { input: classes.pickerInput },
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <RSButton
                                      style={{
                                        background: 'none',
                                      }}
                                      className="d-flex"
                                      color="none"
                                      onClick={() => this.generalToggler('cutOffCalendar', 'cutOffIndicator')}
                                      type="button"
                                      ref={(c) => { this.cutOff = c; }}
                                      id="cut-off-calendar">
                                      <CalendarToday
                                        className={`calendar-toggle-icon ${cutOffIndicator && 'indicate'}`}
                                      />
                                    </RSButton>
                                  </InputAdornment>
                              ),
                              }}
                              label={<div> <span style={{color:'#ff0000'}}> *</span> Cut off Date</div>}
                              variant="outlined"
                              value={cutOffDate && toDefaultISO(cutOffDate)}
                            />
                          </form>
                          <form
                            className={classes.timePicker}
                            onChange={value => this.handleCutOffTime(value)}
                            noValidate
                          >
                            <TextField
                              className={classes.textField}
                              defaultValue={currentTime}
                              id="time"
                              autoComplete="off"
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 60 }}
                              InputProps={{ className: classes.inputTimePicker }} //eslint-disable-line
                              label={<div> <span style={{color:'#ff0000'}}> *</span> Cut Off Time</div>}
                              type="time"
                              variant="outlined"
                            />
                          </form>
                        </div>
                        <Popover
                          className="calendar-popover"
                          placement="bottom"
                          isOpen={deliveryCalendar}
                          toggle={() => this.generalToggler('deliveryCalendar', 'deliveryIndicator')}
                          target="delivery-calendar">
                          <PopoverBody>
                            <Calendar
                              activeStartDate={new Date()}
                              calendarType="US"
                              onClickDay={value => this.handleDeliveryDate(value)}
                              tileClassName={({ date, view }) => {
                                if (view !== 'month') return '';
                                const { enabled, now, old } =
                                  getDeliveryScheduleStatus(deliverySchedule, date);
                                const className = compact([
                                  'date',
                                  'tile',
                                  toISO(date) === toISO(deliveryDate) && 'active pro',
                                  old && 'old',
                                  now && 'now',
                                  enabled && 'select',
                                  (!enabled || old) && 'disabled',
                                ]).join(' ');
                                return className;
                              }}
                              tileDisabled={({ date, view }) => {
                                if (view !== 'month') return false;
                                const { enabled, old } =
                                getDeliveryScheduleStatus(deliverySchedule, date);
                                return !enabled || old;
                              }}
                              value={deliveryDate || null}
                            />
                          </PopoverBody>
                        </Popover>
                        <Popover
                          className="calendar-popover"
                          placement="bottom"
                          isOpen={cutOffCalendar}
                          toggle={() => this.generalToggler('cutOffCalendar', 'cutOffIndicator')}
                          target="cut-off-calendar">
                          <PopoverBody>
                            <Calendar
                              tileClassName={({ date, view }) => {
                                if (view !== 'month') return '';
                                const className = compact([
                                  'date',
                                  'tile',
                                  toISO(date) === toISO(cutOffDate) && 'active pro',
                                ]).join(' ');
                                return className;
                              }}
                              calendarType="US"
                              onClickDay={value => this.handleCutOffDate(value)}
                              value={cutOffDate || null}
                            />
                          </PopoverBody>
                        </Popover>
                      </div>
                    )}
                    <TextField
                      className="customer-subject mt-2"
                      fullWidth
                      placeholder="Subject"
                      id="subject"
                      InputProps={{
                        classes: {
                          input: isInvalid ? classes.red : '',
                        },
                      }}
                      name="subject"
                      onChange={this.handleChangeSubject}
                      value={subjectText}
                      variant="outlined"
                    />
                  </div>
                </div>
                <div className="quill-wrapper">
                  {/* <div className="section-title mb-2">
                    {formatMessage('Body')}
                  </div> */}
                  <ReactQuill
                    className="mb-2"
                    onChange={this.handleChangeComposer}
                    theme="snow"
                    value={composerText}
                    placeholder="Type the message you wish to broadcast here"
                  />
                  <div className="broadcast-schedule">
                    <div className="broadcast-send">
                      <div className="broadcast-text-link">
                        { isVendor ? formatMessage('Send Update Availability Link'): formatMessage('Send Broadcast Ordering Link')}
                      </div>
                      <div className="broadcast-buttons">
                        <Button
                          className="btn-send"
                          classes={{
                            label: classes.sendBtnLabel,
                            root: classes.sendBtn,
                          }}
                          disabled={readOnly || !recipient ||
                            deliveryDate === null || deliveryDate === '' || cutOffTime === '' || cutOffDate === null || broadcastRequesting}
                          onClick={this.broadcast}
                          variant="contained"
                        >
                          {formatMessage('Send')}
                          <Send classes={{ root: classes.rightIcon }} />
                        </Button>
                        <Button
                          classes={{
                            label: classes.cancelBtnLabel,
                            root: classes.cancelBtn,
                          }}
                          variant="contained"
                        >
                          {formatMessage('Cancel')}
                        </Button>
                      </div>
                    
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Paper>
        </div>
        <div className="mid-action-group d-flex">
          <TagsInput
            formatMessage={formatMessage}
            onChange={this.onChangeTags}
            onEditTag={this.onEditTags}
            prefix={isVendor ? 'v-' : 'c-'}
            size="medium"
            tags={userBroadCastGroups}
          />
          <Button
            className={classes.statsBtn}
            onClick={this.handleBroadcastStatsOpen}
            variant="contained"
          >
            {formatMessage('View Sent Broadcast Stats')}
          </Button>
        </div>
        <hr />
        <BroadcastStatsDialog
          isVendor={isVendor}
          onClose={this.handleBroadcastStatsClose}
          open={broadcastStatsOpen}
        />
      </div>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('users', 'userBroadCastGroups')(state, 'userBroadCastGroups'),
  ...selectState('users', 'editBroadCastGroups')(state, 'editBroadCastGroups'),
  broadcastRequesting: getRequestingSelector('mails', 'broadcast')(state),
});

const mapDispatchToProps = dispatch => ({
  ...dispatchRestMaker([
    [broadCastGroupActions, 'userBroadCastGroup'],
    [singleBroadCastGroupActions, 'singleBroadCastGroup'],
  ])(dispatch),
  broadcast: payload => dispatch(broadcastActions.create(payload)),
});

export default compose(
  setDisplayName('Composer'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
  immutableToJS,
  injectIntl,
)(Composer);
